// Shared Market News contract between mbs-api (MIG-7920), the admin UI (MIG-7922), and the
// banner (MIG-7923). Replaces the legacy stored-HTML `mhNews` shape (`ILegacyMarketNews`):
// items are typed and structured, and clients render markup from `type` + fields.

export const MARKET_NEWS_TYPES = ["text", "link", "voice_alert"] as const;
export type TMarketNewsType = (typeof MARKET_NEWS_TYPES)[number];

export const ALERT_TYPES: readonly TMarketNewsType[] = ["voice_alert"];

/** True when an item's `type` is one rendered as a critical alert. */
export const isAlertType = (type: TMarketNewsType): boolean => ALERT_TYPES.includes(type);

export type TRateAlertDirection = "lock" | "float";

interface IMarketNewsBase {
    _id?: string;
    type: TMarketNewsType;
    /** Plain-text headline/copy — never HTML. */
    body?: string;
    /** The calendar day the item is for (normalized to UTC midnight) — read filter + sort key. */
    date: Date | string;
    /** Optional display time-of-day, e.g. "9:30 AM". */
    time?: string;
    /** Publish filter (legacy `mhNews.isLive = 1`); named `published` to match other content models. */
    published: boolean;
    /** Admin user id from the JWT. */
    createdBy: string;
    createdAt?: Date | string;
    updatedAt?: Date | string;
}

export interface IMarketNewsText extends IMarketNewsBase {
    type: "text";
}

/** Daily Update, Market Wrap, Report Card, Newsletter, Mauldin, Expressway. */
export interface IMarketNewsLink extends IMarketNewsBase {
    type: "link";
    linkUrl: string;
    linkLabel: string;
}

export interface IMarketNewsVoiceAlert extends IMarketNewsBase {
    type: "voice_alert";
    audioUrl: string;
    audioDurationSec?: number;
}

export type IMarketNews = IMarketNewsText | IMarketNewsLink | IMarketNewsVoiceAlert;

/**
 * Flattened persistence shape (Mongo schema / DTOs): every variant field optional.
 * API responses and the `updateNews` socket payload narrow to the discriminated `IMarketNews`.
 */
export interface IMarketNewsDocument extends IMarketNewsBase {
    linkUrl?: string;
    linkLabel?: string;
    audioUrl?: string;
    audioDurationSec?: number;
}

/** Latest DialMyCalls recording, proxied server-side for the admin's voice-alert preview. */
export interface IDialMyCallsRecording {
    url: string;
    date: Date | string;
    durationSec?: number;
}
